import React, { useContext, useEffect } from "react";
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  LinearProgress,
  Stack
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import urlcat from "urlcat";
import useQueryParams from "../../Hooks/useQueryParams";
import {
  AUTH_STATUS,
  EmployeeSignInput,
  EmployeeSigninResponse
} from "../../Graphql/types/auth";
import AuthContext from "../../Contexts/authContext";
import { EmployeeSigninQuery } from "../../Graphql/querries/authQuery";
import { fetchGraphQL } from "../../Graphql/utils";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import { setLocalStorageElement } from "../../Utils/localStorageUtils";
import { LOCAL_STORAGE_KEYS } from "../../Enums/localStorage";
import { SET_PASSWORD } from "../Routes";

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters long")
    .matches(/[0-9]/, "Password must contain a number")
    .matches(/[a-zA-Z]/, "Password must contain a letter")
    .required("Password is required")
});

const Login: React.FC = () => {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<EmployeeSignInput>({
    resolver: yupResolver(validationSchema)
  });
  const navigate = useNavigate();
  const { authContextInfo } = useContext(AuthContext);
  const { redirectPage } = useQueryParams();
  const { setAlert } = useContext(AlertBannerContext);

  const { mutate, isPending } = useMutation<
    EmployeeSigninResponse,
    Error,
    EmployeeSignInput
  >({
    mutationFn: (data) =>
      fetchGraphQL(EmployeeSigninQuery, { ...data }, setAlert),
    mutationKey: ["employeeSignin"],
    onSuccess: (response) => {
      const { accessToken, token, employeeId, isVerified } =
        response.employeeSignin;
      setLocalStorageElement(LOCAL_STORAGE_KEYS.TOKEN, token);
      setLocalStorageElement(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      setLocalStorageElement(LOCAL_STORAGE_KEYS.EMPLOYEE_ID, employeeId);

      // if user have verified their account, redirect to reset password page
      if (!isVerified) {
        navigate(
          urlcat(SET_PASSWORD, employeeId, {
            redirectPage
          })
        );
      } else {
        navigate(redirectPage || "/");
      }
    }
  });

  const handleSignin = (formData: EmployeeSignInput) => {
    mutate(formData);
  };

  // Always redirect to previous page if user already logged in
  useEffect(() => {
    if (authContextInfo.auth.status === AUTH_STATUS.AUTHENTICATED) {
      navigate(redirectPage || "/");
    }
  }, [authContextInfo]);

  return (
    <Container maxWidth="xs" style={{ width: "100%", height: "100vh" }}>
      <Box textAlign="center" mt={5} gap="2em">
        <Typography variant="h5" gutterBottom>
          Login to access admin dashboard
        </Typography>
        <form onSubmit={handleSubmit(handleSignin)}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                fullWidth
                margin="normal"
                variant="outlined"
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Password"
                type="password"
                fullWidth
                margin="normal"
                variant="outlined"
                error={Boolean(errors.password)}
                helperText={errors.password?.message}
              />
            )}
          />
          {isPending && (
            <Stack width="100%" paddingBottom="1em" alignSelf="center">
              <LinearProgress />
            </Stack>
          )}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
          >
            Login
          </Button>
        </form>
      </Box>
    </Container>
  );
};

export default Login;
